import curiumLogo from '@client/assets/curium-logo.svg';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { message } from '@client/components/_ui/feedback/message';
import { PageLoading } from '@client/components/_ui/feedback/PageLoading';
import { AuthLoadingScreen } from '@client/components/auth/AuthLoadingScreen';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useAuthStore } from '@client/stores/AuthStore';
import { trpcClient } from '@client/trpc/client';
import { capture } from '@client/utils/analytics';
import { getAuth0Client, handleRedirectCallback } from '@client/utils/auth/auth0Client';
import { AuthError, type AuthErrorType, handleAuthError, identifyErrorType } from '@client/utils/auth/authErrorHandler';
import { beginSignIn } from '@client/utils/auth/signInMarker';
import { debug, error, info, warn } from '@client/utils/logger';
import { extractPathParams } from '@client/utils/routing/extractPathParams';
import * as Sentry from '@sentry/react';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, Home, LogIn } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import invariant from 'tiny-invariant';

export const Route = createFileRoute('/(authentication)/callback')({ component: AuthCallback });

function parseRequestedOrgId(value: string | undefined): bigint | undefined {
    if (!value) return undefined;

    const trimmedValue = value.trim();
    if (!/^-?\d+$/.test(trimmedValue)) {
        warn('[AuthCallback] Ignoring malformed requested org id', { requestedOrgId: value });
        return undefined;
    }

    return BigInt(trimmedValue);
}

/** Identity denials whose message says what to do, instead of the generic organisation-access text. */
const IDENTITY_ERROR_TYPES: readonly AuthErrorType[] = ['identity_mismatch', 'sso_required', 'invite_not_accepted'];

// curium:allow-route-value-export — eagerly installed to avoid the callback route loading failure
export function AuthCallback() {
    const queryClient = useQueryClient();
    const authStore = useAuthStore();
    const authInProgressRef = useRef(false);
    const navigate = useNavigate();
    const sessionErrorTypes = useMemo<readonly AuthErrorType[]>(
        () => [
            'missing_refresh_token',
            'invalid_grant',
            'missing_id_token',
            'invalid_state',
            'token_expired',
            'login_required',
        ],
        [],
    );
    const [errorData, setErrorData] = useState<
        | {
              message: string;
              type: AuthErrorType;
              userEmail?: string;
          }
        | undefined
    >(undefined);

    // Helper function to get user email from Auth0 claims
    const getUserEmailFromClaims = async (): Promise<string | undefined> => {
        try {
            const claims = await getAuth0Client().getIdTokenClaims();
            const email = claims?.['upn'] || claims?.['email'] || claims?.['preferred_username'];
            if (email) {
                debug('User claims retrieved:', {
                    upn: claims?.['upn'],
                    email: claims?.['email'],
                    preferred_username: claims?.['preferred_username'],
                });
            } else {
                debug('User claims retrieved: no email');
            }
            return email;
        } catch (e) {
            error('getUserEmailFromClaims error', e);

            return undefined;
        }
    };

    // Helper function to check if error should show email
    const isAccessRelatedError = (errorType: string, errorMessage: string): boolean => {
        const accessErrorTypes = [
            'user_not_found',
            'user_disabled',
            'access_denied',
            'no_org_assigned',
            'sso_linked_to_old_profile',
            'no_role_assigned',
            'org_mismatch',
            'unknown',
            'no_access_to_org',
            'org_disabled',
            'email_not_verified',
            'user_has_not_allowed_migration_to_sso',
        ];
        return accessErrorTypes.includes(errorType) || errorMessage.toLowerCase().includes('user not found');
    };

    // Helper function to handle auth error with email enhancement
    const handleAuthErrorWithEmail = async (
        error: AuthError | unknown,
        context: string,
        options?: { captureToSentry?: boolean },
    ): Promise<void> => {
        // Get email from URL params first, then fall back to Auth0 claims
        const searchParams = new URLSearchParams(window.location.search);
        const emailParam = searchParams.get('email');
        const userEmail = emailParam || (await getUserEmailFromClaims());
        const { captureToSentry = true } = options || {};

        let authError: AuthError;
        if (error instanceof AuthError) {
            authError = error;
        } else {
            authError = handleAuthError(error, {
                showMessage: false,
                captureToSentry,
                context,
            });
        }

        const shouldShowEmail = isAccessRelatedError(authError.type, authError.message);
        const isSessionError = sessionErrorTypes.includes(authError.type);

        if (userEmail && shouldShowEmail && !isSessionError) {
            setErrorData({
                message: authError.message,
                userEmail,
                type: authError.type,
            });
        } else {
            setErrorData({ message: authError.message, type: authError.type });
        }
    };

    useEffect(() => {
        const asyncWrapper = async () => {
            Sentry.getCurrentScope().setTransactionName('Login Callback');
            queryClient.clear();

            if (authInProgressRef.current) {
                return;
            }

            let endSignIn: (() => void) | undefined;
            try {
                authInProgressRef.current = true;

                const searchParams = new URLSearchParams(window.location.search);
                const errorParam = searchParams.get('error');
                const errorDescription = searchParams.get('error_description');

                info('[AuthCallback] errorParam', errorParam ? 'true' : 'false');

                if (errorParam) {
                    let errorCode: AuthErrorType;
                    let emailFromError: string | undefined;
                    let errorFromUrl: { message?: string } | undefined;

                    info('errorParam', errorParam);

                    // Check if this is an access_denied error from our Auth0 Action
                    if (errorParam === 'access_denied' && errorDescription) {
                        try {
                            // Try to parse JSON from error_description
                            const errorData = JSON.parse(errorDescription);
                            errorCode = errorData.code as AuthErrorType;
                            emailFromError = errorData.email;
                            errorFromUrl = errorData;
                            info('errorData', errorData);
                        } catch {
                            // Fallback: treat error_description as the error code
                            errorCode = errorDescription as AuthErrorType;
                            info('errorCode', errorCode);
                        }
                    } else {
                        // For other error types, use the error parameter directly
                        errorCode = errorParam as AuthErrorType;
                    }

                    if (errorCode === 'payment_required_for_login') {
                        navigate({ to: '/payment-pending' });
                        return;
                    }

                    if (errorCode === 'trial_expired') {
                        navigate({ to: '/trial-expired' });
                        return;
                    }

                    // Get user email from parsed error first, then URL params, then Auth0 claims
                    const emailParam = searchParams.get('email');
                    const errorMessageFromAction = errorFromUrl?.message;
                    const userEmail = emailFromError || emailParam || (await getUserEmailFromClaims());
                    const shouldShowEmail = isAccessRelatedError(errorCode, errorMessageFromAction || '');
                    const isSessionError = sessionErrorTypes.includes(errorCode);

                    info('errorMessageFromAction', errorMessageFromAction);

                    const customMessage = errorMessageFromAction
                        ? errorMessageFromAction
                        : userEmail && shouldShowEmail
                          ? `This account doesn't have access to this organisation. If this is not the correct account, please click "Try again" to log in with a different account.`
                          : undefined;

                    const authError = new AuthError(errorCode, customMessage);

                    // If we have an email and should show it, set the error data directly
                    if (userEmail && shouldShowEmail && !isSessionError) {
                        setErrorData({
                            message: authError.message,
                            userEmail,
                            type: authError.type,
                        });
                    } else {
                        await handleAuthErrorWithEmail(authError, 'urlParam');
                    }
                    return;
                }

                const appStateWrapper = (await handleRedirectCallback()) as {
                    appState?: { redirectTo?: string; requestOrgId?: string };
                };

                const redirectTo = decodeURIComponent(appStateWrapper?.appState?.redirectTo || '');

                // Honour admin-login intent and deep-link orgs in the bootstrap call so the server
                // doesn't silently land the user in their default regular org and trigger a
                // RequireAuth bounce-back on /admin-console.
                // Query and hash (e.g. `#whats-new/<id>`) are carried through to the final
                // navigation below; only the path decides the organisation.
                const redirectToPath = redirectTo.split(/[?#]/)[0] ?? '';
                const redirectToOrgMatch = extractPathParams(redirectToPath, '/console/$orgId', { fuzzy: true });
                const requestedOrgIdRaw =
                    appStateWrapper?.appState?.requestOrgId ||
                    redirectToOrgMatch?.orgId ||
                    (redirectToPath.includes('/admin-console') ? '-1' : undefined);
                const requestedOrgId = parseRequestedOrgId(requestedOrgIdRaw);

                const accessToken = await authStore.getAccessToken({ auth0CacheMode: 'on' });

                debug('[AuthCallback] calling authStore.login');
                // From here this tab holds a fresh token on /callback, a non-protected route. The
                // marker makes it ignore another tab's token-expired until `finally`, which runs
                // after navigate() has queued the move to the landing page (CUR-2637).
                endSignIn = beginSignIn();
                const bootstrapResult = await authStore.login(
                    accessToken,
                    requestedOrgId !== undefined ? { requestedOrgId } : undefined,
                );

                if (!bootstrapResult.ok) {
                    // Map structured server errors to the existing auth error UI flow.
                    const errorType: AuthErrorType =
                        bootstrapResult.error === 'sso_linked_to_old_profile'
                            ? 'sso_linked_to_old_profile'
                            : bootstrapResult.error === 'no_access_to_org'
                              ? 'no_org_assigned'
                              : bootstrapResult.error === 'org_disabled'
                                ? 'org_disabled'
                                : bootstrapResult.error === 'user_disabled'
                                  ? 'user_disabled'
                                  : bootstrapResult.error === 'email_not_verified'
                                    ? 'email_not_verified'
                                    : bootstrapResult.error === 'identity_mismatch'
                                      ? 'identity_mismatch'
                                      : bootstrapResult.error === 'sso_required'
                                        ? 'sso_required'
                                        : bootstrapResult.error === 'invite_not_accepted'
                                          ? 'invite_not_accepted'
                                          : bootstrapResult.error === 'payment_required_for_login'
                                            ? 'payment_required_for_login'
                                            : bootstrapResult.error === 'trial_expired'
                                              ? 'trial_expired'
                                              : bootstrapResult.error === 'no_role_assigned'
                                                ? 'no_role_assigned'
                                                : bootstrapResult.error === 'support_session_required'
                                                  ? 'support_session_required'
                                                  : 'unknown';

                    if (errorType === 'payment_required_for_login') {
                        navigate({ to: '/payment-pending' });
                        return;
                    }

                    if (errorType === 'trial_expired') {
                        // Name the org the bootstrap refused: the store still holds the previous one.
                        navigate({ to: '/trial-expired', search: { orgId: requestedOrgId } });
                        return;
                    }

                    setErrorData({
                        message: bootstrapResult.message,
                        type: errorType,
                        userEmail: bootstrapResult.email,
                    });
                    return;
                }

                capture('auth_login', { platform: 'web' });

                // Record successful login on server with atomic timestamp updates
                try {
                    await trpcClient.user.recordSuccessfulLogin.mutate({
                        redirectTo: redirectTo || undefined,
                        metadata: {
                            platform: 'web',
                            deviceType: navigator.userAgent.includes('Mobile') ? 'mobile' : 'desktop',
                        },
                    });
                } catch (error) {
                    // Log error but don't block the login flow
                    // The server has BullMQ retry logic as a fallback
                    warn('Failed to record login on server:', error);
                }

                const org = useAuthStore.getState().org;
                invariant(org, 'org is required');

                if (redirectTo && !redirectTo.includes('/login')) {
                    navigate({ href: redirectTo });
                } else {
                    if (org.id === SYSTEM_ORG_IDS.SYSTEM_CONSOLE) {
                        navigate({ to: '/admin-console' });
                    } else {
                        navigate({ to: '/console/$orgId', params: { orgId: org.id } });
                    }
                }
            } catch (e) {
                const authErrorType =
                    e instanceof AuthError ? e.type : e instanceof Error ? identifyErrorType(e) : 'unknown';

                const shouldCaptureToSentry =
                    !sessionErrorTypes.includes(authErrorType) &&
                    authErrorType !== 'network_error' &&
                    authErrorType !== 'temporary_auth_error';

                if (shouldCaptureToSentry) {
                    Sentry.captureException(e, {
                        extra: {
                            location: 'authCallback',
                            authErrorType,
                        },
                    });
                }

                if (authErrorType === 'invalid_state') {
                    // This is a known issue with Auth0 where the state is invalid.
                    // See: https://community.auth0.com/t/invalid-state-on-reload-auth0-callback-url-using-auth0-spa-js-and-angular-8/36469/10

                    await authStore.logout('invalid_state');
                    return;
                } else {
                    error('[AuthCallback] Error in authCallback: ', e);
                    await handleAuthErrorWithEmail(e, 'authCallback', { captureToSentry: false });
                    return;
                }
            } finally {
                endSignIn?.();
                authInProgressRef.current = false;
                message.destroy();
            }
        };

        void asyncWrapper();
    }, []);

    if (!errorData) {
        return <AuthLoadingScreen title="Signing you in" hint="Securely authenticating your account" />;
    }

    return (
        <PageLoading>
            <div className="w-full px-4 py-12 md:py-0">
                <div className="mx-auto w-full max-w-md rounded-lg border border-separator bg-card p-6 text-center sm:p-10">
                    <div className="mb-6">
                        <img src={curiumLogo} alt="Curium" className="mx-auto h-10 w-auto dark:invert" />
                    </div>
                    <div className="flex flex-col items-center space-y-6">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-danger-subtle">
                            <AlertCircle className="h-10 w-10 text-danger" />
                        </div>
                        <div className="text-center">
                            {/* curium:allow-heading — login callback error screen, outside the app shell */}
                            <h2 className="mb-2 font-bold text-2xl text-foreground">Authentication error</h2>
                            {errorData.userEmail &&
                            !sessionErrorTypes.includes(errorData.type) &&
                            !IDENTITY_ERROR_TYPES.includes(errorData.type) ? (
                                <div className="mb-6 text-base text-muted-foreground">
                                    <p className="mb-2">Access denied for:</p>
                                    <p className="mb-4 font-semibold text-foreground text-lg">{errorData.userEmail}</p>
                                    <p className="mb-2">
                                        {/* CUR-2873: the server explains a sign-in stuck on an old profile. */}
                                        {errorData.type === 'sso_linked_to_old_profile'
                                            ? errorData.message
                                            : "This account doesn't have access to this organisation."}
                                    </p>
                                    <p className="text-sm">
                                        If this is not the correct account, please click "Try again" below to log in
                                        with a different account.
                                    </p>
                                </div>
                            ) : (
                                <div className="mb-6 text-base text-muted-foreground">
                                    <p className="mb-2">{errorData.message}</p>
                                    {sessionErrorTypes.includes(errorData.type) ? (
                                        <p className="text-sm text-subtle-foreground">
                                            We'll start a fresh login when you click "Try again" so you can continue
                                            where you left off.
                                        </p>
                                    ) : null}
                                </div>
                            )}
                        </div>
                        <div className="flex space-x-4">
                            <Button
                                type="primary"
                                icon={<LogIn />}
                                onClick={() => {
                                    authStore.logout('error').then(() => {
                                        navigate({ to: '/login', search: { forceReauth: 1 } });
                                    });
                                }}
                            >
                                Try again
                            </Button>
                            <Button type="secondary" icon={<Home />} onClick={() => navigate({ to: '/' })}>
                                Go home
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </PageLoading>
    );
}
